import {useEffect, useMemo, useCallback, useState} from 'react';
import {useSharedValue, createSharedValueKey} from '@epic-core/hooks';
import {marketoApi} from './marketo.api';
import {isError} from '@epic-mw/error-types';
import {AccountRes} from 'epic-ue-shared/src/types/account.types';

interface MarketoLead {
    isSuccess: boolean;
    leadData: MarketoLeadData[];
    leadStatus: string;
}

interface MarketoLeadData {
    id: number;
    academicInstitutionorProgramName: string;
    globalEmailOptIn: number;
    industry: string;
    email: string;
    leadSource: string;
    updatedAt: string;
    Last_Trainer_Application__c: string;
    Last_Center_Application__c: string;
}

export const newsletterAccountKey = createSharedValueKey<AccountRes>('newsletterAccount', {});
export const marketoLeadKey = createSharedValueKey<MarketoLead>('marketoLead', {} as MarketoLead);
export const NewsletterEngagementActive = createSharedValueKey('newsletterEngagementActive', false);
export const HideGlobalEmailOptIn = createSharedValueKey('hideGlobalEmailOptIn', false);
interface NewsletterAccountRes {
    account?: AccountRes;
    setAccount: (a: AccountRes) => void;
}
export const useNewsletterAccount = (): NewsletterAccountRes => {
    const [account, setAccount] = useSharedValue(newsletterAccountKey);

    return {
        account,
        setAccount
    };
};

let loadingState = {
    loading: false,
    loaded: false,
    error: ''
};

export const useMarketoLead = (
    disabled?: boolean
): {
    reset: () => void;
    loading: any;
    marketoLead: MarketoLead;
} => {
    const {account} = useNewsletterAccount();
    const [marketoLead, setMarketoLead] = useSharedValue<MarketoLead>(marketoLeadKey);

    const reset = useCallback(() => {
        setMarketoLead({} as MarketoLead);
        loadingState = {
            loading: false,
            loaded: false,
            error: ''
        };
    }, [setMarketoLead, loadingState]);

    useEffect(() => {
        if (
            disabled ||
            !account?.isLoggedIn ||
            (marketoLead.leadStatus && !loadingState.error) ||
            loadingState.loading ||
            loadingState.loaded
        ) {
            return;
        }
        async function fetchData() {
            try {
                loadingState.loading = true;
                const lead: MarketoLead = await marketoApi.getLead();

                loadingState.loading = false;
                loadingState.loaded = true;
                setMarketoLead(lead);
            } catch (ex) {
                console.error('Failed to fetch marketo lead data', ex);
                loadingState.loading = false;
                loadingState.loaded = true;
                loadingState.error = (isError(ex) && ex.message) || '';
                setMarketoLead({} as MarketoLead);
            }
        }
        fetchData();
    }, [account, marketoLead, loadingState]);

    return useMemo(() => {
        return {
            marketoLead,
            reset,
            loading:
                loadingState.loading ||
                (account?.isLoggedIn && !marketoLead.leadStatus && !loadingState.error)
        };
    }, [
        account?.isLoggedIn,
        marketoLead.leadStatus,
        loadingState.loading,
        loadingState.loaded,
        loadingState.error
    ]);
};

export const useLatestMarketoLead = (disabled?: boolean): MarketoLeadData => {
    const {marketoLead} = useMarketoLead(disabled);
    const leadData = marketoLead.leadData || [];
    if (!leadData.length) return {} as MarketoLeadData;

    const latestLead = leadData.reduce((a, b) => {
        return a.updatedAt > b.updatedAt ? a : b;
    });
    return latestLead;
};

export const useMarketoLeadFlag = (
    flags: string[],
    disabled?: boolean
): string | boolean | number => {
    const {marketoLead} = useMarketoLead(disabled);
    if (!flags || !flags.length) return false;

    const leadData = marketoLead.leadData || [];

    let response = false;

    flags.forEach((flag) => {
        for (let i = 0; i < leadData.length; i++) {
            const data = leadData[i];
            if (data[flag] && !!data[flag]) {
                response = data[flag];
                break;
            }
        }
    });

    return response;
};

export const useMarketoGlobalOptIn = (disabled?: boolean): boolean => {
    const globalEmailOptIn = useMarketoLeadFlag(['globalEmailOptIn'], disabled);
    return globalEmailOptIn === 1;
};

interface NewsletterSubmission {
    [key: string]: boolean;
}
export const marketoNewsletterSubmissionKey = createSharedValueKey<NewsletterSubmission>(
    'marketoNewsletterSubmission',
    {} as NewsletterSubmission
);

const type = 'global';

export const useMarketoNewsletterSubscribe = (): {
    loaded: boolean;
    subscribed: boolean;
    subscribe: (
        email: string,
        industry?: string,
        params?: {[p: string]: boolean | number | string}
    ) => Promise<void>;
    loading: boolean;
    error?: string;
} => {
    const [submissions, setSubmissions] = useSharedValue<NewsletterSubmission>(
        marketoNewsletterSubmissionKey
    );
    const [loadingState, setLoadingState] = useState<{
        loading: boolean;
        loaded: boolean;
        error?: string;
    }>({loading: false, loaded: false, error: ''});

    const subscribed = submissions[type] || false;

    const subscribe = useCallback(
        async (
            email: string,
            industry?: string,
            params?: {[key: string]: boolean | number | string}
        ) => {
            try {
                setLoadingState({loading: true, loaded: false});

                const data: {status: number; isSuccess: boolean; type: string} =
                    (await marketoApi.subscribeToNewsletter(type, email, industry, params)) || {};
                setSubmissions(Object.assign({}, submissions, {[type]: data.isSuccess || false}));

                setLoadingState({loading: false, loaded: true});
            } catch (ex) {
                console.error('Failed to subscribe to newsletter', type, ex);
                const error: string = (isError(ex) && ex.message) || '';
                setLoadingState({loading: false, loaded: true, error});
            }
        },
        [submissions, setSubmissions, loadingState, type]
    );

    return useMemo(() => {
        return {
            subscribed,
            ...loadingState,
            subscribe
        };
    }, [subscribed, loadingState.loading, loadingState.loaded]);
};

export const useUpdateLead = (): {
    loaded: boolean;
    success?: boolean;
    loading: boolean;
    error?: string;
    updateLead: (data: any) => Promise<void>;
} => {
    const [loadingState, setLoadingState] = useState<{
        loading: boolean;
        loaded: boolean;
        error?: string;
        success?: boolean;
    }>({loading: false, loaded: false, error: '', success: false});

    const updateLead = useCallback(
        async (data) => {
            try {
                setLoadingState({loading: true, loaded: false});

                const success = await marketoApi.updateLead(data);

                setLoadingState({loading: false, loaded: true, success});
            } catch (ex) {
                console.error('Failed to update lead', ex);
                const error: string = (isError(ex) && ex.message) || '';
                setLoadingState({loading: false, loaded: true, error});
            }
        },
        [loadingState, setLoadingState]
    );

    return useMemo(() => {
        return {
            ...loadingState,
            updateLead
        };
    }, [loadingState.loading, loadingState.loaded]);
};
